import ApiIcon from '@mui/icons-material/Api';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Step,
  StepLabel,
  Stepper,
} from '@mui/material';
import axios from 'axios';
import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { getVendorAPIData, postAPIVendorForm } from '../../../../../api/invoicing/invoicingVendors';
import { getVendors } from '../../../../../redux/slices/invoicing';
import { useDispatch, useSelector } from '../../../../../redux/store';
import { AwaitButton } from '../../../../../reusable-components/await-button';
import Iconify from '../../../../../reusable-components/iconify';
import { useSnackbar } from '../../../../../reusable-components/snackbar';
import { BookingTypeStep } from './BookingTypeStep';
import { VendorSelectStep } from './VendorSelectStep';
import CloseIconButton from '../../../../../reusable-components/basic-buttons/CloseIconButton';

const LAST_STEP = 1;
export default function VendorAPIModal() {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshLoading, setIsRefreshLoading] = useState(false);
  const [data, setData] = useState([]);
  const { enqueueSnackbar } = useSnackbar();
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [activeStep, setActiveStep] = useState(0);
  const [progress, setProgress] = useState(0);
  const [selectedBookingTypeId, setSelectedBookingTypeId] = useState(0);
  const [counts, setCounts] = useState({ newCount: 0, updatedCount: 0 });
  const { editableVendorBookingTypes } = useSelector((state) => state.invoicing.data.invoicingVendors);
  const abortControllerRef = useRef(null);
  const isMountedRef = useRef(true);
  const filteredEditableVendorBookingTypes = useMemo(() => (editableVendorBookingTypes ?? []).filter(type => type.facilityAccountId === "SageIntacctId" || type.facilityAccountId === "QuickbooksId"), [editableVendorBookingTypes]);
  const singleBookingType = filteredEditableVendorBookingTypes.length === 1;

  const selectedBookingTypeLabel = useMemo(() => editableVendorBookingTypes?.find((type) => type.id === selectedBookingTypeId)?.name || '', [editableVendorBookingTypes, selectedBookingTypeId]);
  const isStepValid = useMemo(() => {
    switch (activeStep) {
      case 0:
        return selectedBookingTypeId > 0;
      case 1:
        return rowSelectionModel.length > 0;
      default:
        return true;
    }
  }, [activeStep, selectedBookingTypeId, rowSelectionModel]);

  const handleClickOpen = () => {
    if (singleBookingType) {
      setSelectedBookingTypeId(filteredEditableVendorBookingTypes[0].id);
      setActiveStep(1); // skip to step 1
    } else {
      setSelectedBookingTypeId(0);
      setActiveStep(0);
    }
    setOpen(true);
  };
  const handleClose = () => {
    abortControllerRef.current?.abort();
    setIsRefreshLoading(false);
    setActiveStep(0);
    setProgress(0);
    setRowSelectionModel([]);
    setSelectedBookingTypeId(0);
    setData([]);
    setOpen(false);
  };
  const handleBack = () => {
    abortControllerRef.current?.abort();
    setIsRefreshLoading(false);
    setProgress(0);
    setActiveStep((prevActiveStep) => (prevActiveStep > 0 ? prevActiveStep - 1 : prevActiveStep));
  };
  const handleNext = async () => {
    if (activeStep === LAST_STEP) {
      await saveVendorData();
    } else {
      await fetchVendorData();
      if (isStepValid) {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
      }
    }
  };
  const fetchVendorData = useCallback(
    async (refresh = false) => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
      abortControllerRef.current = new AbortController();
      const { signal } = abortControllerRef.current;

      try {
        if (refresh) {
          setIsRefreshLoading(true);
          setProgress(0);
          const timer = setInterval(() => {
            setProgress((prev) => (prev >= 95 ? prev : prev + 1));
          }, 310);
          const res = await getVendorAPIData(selectedBookingTypeId, true, signal);
          clearInterval(timer);
          if (!isMountedRef.current) return;

          if (res?.status === 200 && res.data?.length > 0) {
            const changedById = new Map(res.data.map((v) => [v.id, v]));
            const updatedCount = data.filter((v) => changedById.has(v.id)).length;
            const newlyAdded = res.data.filter((v) => !data.some((pv) => pv.id === v.id));
            setData((prev) => {
              const merged = prev.map((v) => (changedById.has(v.id) ? changedById.get(v.id) : v));
              return [...merged, ...newlyAdded];
            });
            setCounts({ newCount: newlyAdded.length, updatedCount });
          }
          setIsRefreshLoading(false);
          setProgress(0);
        } else {
          setIsLoading(true);
          const res = await getVendorAPIData(selectedBookingTypeId, false, signal);
          if (res?.status === 200 && res.data) {
            setData(res.data);
          }
          setIsLoading(false);
          if (!signal.aborted) fetchVendorData(true);
        }
      } catch (e) {
        if (
          axios.isCancel(e) ||
          (typeof e === 'string' && e.startsWith('Request canceled')) ||
          (typeof e === 'string' && e === 'Something went wrong') // backend 500 after abort
        )
          return;

        if (isMountedRef.current) {
          setIsLoading(false);
          setIsRefreshLoading(false);
          setProgress(0);
        }
        throw e;
      }
    }, [selectedBookingTypeId]);

  const saveVendorData = async () => {
    const response = await postAPIVendorForm(selectedBookingTypeId, rowSelectionModel);
    const message = response.data.message ?? 'Error adding vendors.';
    const idsMatch = message.match(/\[Ids: ([\d,]+|all)\]/);
    enqueueSnackbar(message.replace(/\s*\[Ids:.*?\]/, '').trim(), {
      variant: response.data.success ? 'success' : 'error',
      persist: response.data.keepFormOpen,
    });

    if (response.data.success) {
      const isAll = idsMatch?.[1] === 'all';
      const ids = isAll ? null : idsMatch?.[1]?.split(',').map(Number);
      dispatch(getVendors(ids));
      handleClose();
    }
  };

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      abortControllerRef.current?.abort();
    };
  }, []);
  useEffect(() => {
    if (singleBookingType && open) fetchVendorData();
  }, [singleBookingType, open]);
  useEffect(() => {
    setRowSelectionModel([]);
    setData([]);
  }, [selectedBookingTypeId]);

  return (
    <>
      <Button
        variant="outlined"
        sx={{ mr: 1 }}
        size="small"
        color="secondary"
        onClick={handleClickOpen}
        startIcon={<ApiIcon />}
      >
        Import Vendors
      </Button>

      <Dialog
        open={open}
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          handleClose();
        }}
        maxWidth={activeStep === LAST_STEP ? 'xl' : 'sm'}
        fullWidth
      >
        <DialogTitle sx={{ textAlign: 'center' }}>
          {activeStep > 0 && !singleBookingType && (
            <IconButton
              title="Back"
              onClick={handleBack}
              style={{
                position: 'absolute',
                left: 8,
                top: 8,
                color: 'gray',
              }}
            >
              <Iconify icon="ic:baseline-arrow-back" width={28} height={28} />
            </IconButton>
          )}
          Import Intacct Vendors
          <CloseIconButton onClick={handleClose} />
        </DialogTitle>
        <DialogContent>
          {!singleBookingType && (
            <Stepper activeStep={activeStep} alternativeLabel color="secondary" width="600px">
              <Step key={'1'}>
                <StepLabel sx={{ color: 'rgba(112, 112, 112, 0.5)', mb: 4 }}>{'Choose Booking Type'}</StepLabel>
              </Step>
              <Step key={'2'}>
                <StepLabel sx={{ color: 'rgba(112, 112, 112, 0.5)', mb: 4 }}>{'Add Vendors'}</StepLabel>
              </Step>
            </Stepper>
          )}
          <Box>
            {activeStep === 0 && (
              <BookingTypeStep
                editableVendorBookingTypes={filteredEditableVendorBookingTypes}
                selectedBookingTypeId={selectedBookingTypeId}
                onSelect={setSelectedBookingTypeId}
              />
            )}
            {activeStep === LAST_STEP && (
              <VendorSelectStep
                selectedBookingTypeLabel={selectedBookingTypeLabel}
                counts={counts}
                setCounts={setCounts}
                isRefreshLoading={isRefreshLoading}
                progress={progress}
                filteredData={data}
                rowSelectionModel={rowSelectionModel}
                setRowSelectionModel={setRowSelectionModel}
                isLoading={isLoading}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <AwaitButton
            variant="contained"
            color="primary"
            size="medium"
            disabled={!isStepValid}
            sx={{ width: 150 }}
            onClick={handleNext}
          >
            {activeStep === LAST_STEP
              ? `Save ${rowSelectionModel.length > 0 ? `(${rowSelectionModel.length})` : ''}`
              : 'Next'}
          </AwaitButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
